import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Checkbox, Dialog } from '@artosera/ui';
import {
  creerLotDepuisDepot,
  NATURES_PIVOT,
  profilerLot,
  TYPES_SOURCE_LISIBLES,
  type NaturePivot,
} from '../../../../lib/correspondance.js';
import {
  lireManifeste,
  lirePageManifeste,
  listerDepots,
  type EntreeManifeste,
} from '../../../../lib/depots.js';
import { formaterOctets } from '../../../../lib/format.js';
import type { LotReprise } from '../../../../lib/reprise.js';
import formStyles from '../../../../styles/form.module.css';
import { cleDepots } from '../DepotsBloc.js';
import { messageReprise } from '../messages.js';
import reprise from '../Reprise.module.css';
import styles from './Correspondance.module.css';

/** Un lot cite au plus dix fichiers d'un même dépôt (`api.md` § 12.16). */
export const FICHIERS_MAX = 10;

const lisible = (fichier: EntreeManifeste): boolean =>
  fichier.type !== null && TYPES_SOURCE_LISIBLES.includes(fichier.type.mime);

/**
 * « Lot depuis un dépôt » : un dépôt prêt, ses fichiers (le manifeste, page
 * par page), la nature principale, puis le profilage. Ces trois temps se
 * suivent, donc ils se numérotent : le suivant n'apparaît qu'une fois le
 * précédent fait. Rien n'est écrit chez la galerie.
 */
export function NouveauLotDepot({
  idGalerie,
  ouvert,
  onFermer,
  onCree,
}: {
  idGalerie: string;
  ouvert: boolean;
  onFermer: () => void;
  onCree: (lot: LotReprise) => void;
}): JSX.Element {
  const { t } = useTranslation('console');
  const [depotId, setDepotId] = useState('');
  const [choisis, setChoisis] = useState<Map<string, EntreeManifeste>>(new Map());
  const [nature, setNature] = useState<NaturePivot>('oeuvres');
  const [page, setPage] = useState(1);
  const [erreur, setErreur] = useState<string | null>(null);
  const [cree, setCree] = useState<LotReprise | null>(null);

  const depots = useQuery({
    queryKey: [...cleDepots(idGalerie), 'pour-un-lot'],
    queryFn: () => listerDepots(idGalerie),
    enabled: ouvert,
  });
  const utilisables = (depots.data ?? []).filter((depot) => depot.utilisable);

  const cleManifeste = ['console', 'galerie', idGalerie, 'reprise', 'depots', depotId, 'manifeste'];
  const index = useQuery({
    queryKey: cleManifeste,
    queryFn: () => lireManifeste(idGalerie, depotId),
    enabled: ouvert && depotId !== '',
  });
  const fichiers = useQuery({
    queryKey: [...cleManifeste, page],
    queryFn: () => lirePageManifeste(idGalerie, depotId, page),
    enabled: ouvert && depotId !== '' && index.isSuccess,
  });
  const pages = index.data?.pages.length ?? 1;
  const acceptes = (fichiers.data ?? []).filter((fichier) => fichier.etat === 'accepte');

  const reinitialiser = (): void => {
    setDepotId('');
    setChoisis(new Map());
    setPage(1);
    setErreur(null);
    setCree(null);
  };

  const creer = useMutation({
    mutationFn: async () => {
      const lot = await creerLotDepuisDepot(idGalerie, {
        depotId,
        fichiers: [...choisis.keys()],
        nature,
      });
      // Le lot existe dès ici : un profilage refusé (mandat absent…) se relance depuis sa fiche.
      try {
        await profilerLot(idGalerie, lot.id);
        return { lot, refus: null };
      } catch (cause) {
        return { lot, refus: messageReprise(t, cause) };
      }
    },
    onSuccess: ({ lot, refus }) => {
      if (refus === null) {
        reinitialiser();
        onCree(lot);
        return;
      }
      setCree(lot);
      setErreur(refus);
    },
    onError: (cause) => setErreur(messageReprise(t, cause)),
  });

  const basculer = (fichier: EntreeManifeste, coche: boolean): void => {
    setChoisis((courants) => {
      const suite = new Map(courants);
      if (coche) suite.set(fichier.id, fichier);
      else suite.delete(fichier.id);
      return suite;
    });
  };

  const fermer = (): void => {
    reinitialiser();
    onFermer();
  };

  return (
    <Dialog
      open={ouvert}
      onOpenChange={(estOuvert) => {
        if (!estOuvert) fermer();
      }}
      title={t('correspondance.nouveau.titre')}
      description={t('correspondance.nouveau.texte')}
      closeLabel={t('correspondance.nouveau.annuler')}
      footer={
        cree ? (
          <Button
            onClick={() => {
              const lot = cree;
              reinitialiser();
              onCree(lot);
            }}
          >
            {t('correspondance.nouveau.ouvrir')}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={fermer}>
              {t('correspondance.nouveau.annuler')}
            </Button>
            <Button
              loading={creer.isPending}
              disabled={choisis.size === 0}
              onClick={() => creer.mutate()}
            >
              {t('correspondance.nouveau.profiler')}
            </Button>
          </>
        )
      }
    >
      <div className={reprise.pile}>
        <section className={styles.temps} aria-labelledby="lot-depot-1">
          <h3 id="lot-depot-1" className={styles.tempsTitre}>
            <span className={styles.tempsNumero} aria-hidden="true">
              1
            </span>
            {t('correspondance.nouveau.depot')}
          </h3>
          <div className={formStyles.champ}>
            <label htmlFor="lot-depot-choix">{t('correspondance.nouveau.depotChoix')}</label>
            <select
              id="lot-depot-choix"
              className={styles.selection}
              value={depotId}
              disabled={creer.isPending || cree !== null}
              onChange={(evenement) => {
                setDepotId(evenement.target.value);
                setChoisis(new Map());
                setPage(1);
                setErreur(null);
              }}
            >
              <option value="">{t('correspondance.nouveau.depotAucunChoix')}</option>
              {utilisables.map((depot) => (
                <option key={depot.id} value={depot.id}>
                  {`${depot.libelle || t('reprise.depots.sansNom')} · ${t('reprise.depots.fichiers', { count: depot.fichiers })}`}
                </option>
              ))}
            </select>
            {depots.isSuccess && utilisables.length === 0 ? (
              <span className={formStyles.aide}>{t('correspondance.nouveau.depotAucun')}</span>
            ) : null}
          </div>
        </section>

        {depotId !== '' ? (
          <section className={styles.temps} aria-labelledby="lot-depot-2">
            <h3 id="lot-depot-2" className={styles.tempsTitre}>
              <span className={styles.tempsNumero} aria-hidden="true">
                2
              </span>
              {t('correspondance.nouveau.fichiers')}
            </h3>
            {index.isLoading || fichiers.isLoading ? (
              <p className={reprise.discret}>{t('reprise.chargement')}</p>
            ) : null}
            {index.isError || fichiers.isError ? (
              <p className={reprise.erreur} role="alert">
                {t('reprise.depots.manifeste.erreur')}
              </p>
            ) : null}
            {fichiers.isSuccess ? (
              <ul
                className={styles.choixFichiers}
                aria-label={t('correspondance.nouveau.fichiers')}
              >
                {acceptes.map((fichier) => {
                  const coche = choisis.has(fichier.id);
                  const lis = lisible(fichier);
                  return (
                    <li key={fichier.id}>
                      <Checkbox
                        id={`lot-fichier-${fichier.id}`}
                        checked={coche}
                        disabled={!lis || (!coche && choisis.size >= FICHIERS_MAX)}
                        onCheckedChange={(valeur) => basculer(fichier, valeur === true)}
                        label={
                          <span className={styles.nomFichier}>
                            <span className={reprise.chemin}>{fichier.chemin}</span>
                            <span className={reprise.discret}>
                              {lis
                                ? formaterOctets(fichier.octets)
                                : t('correspondance.nouveau.illisible')}
                            </span>
                          </span>
                        }
                      />
                    </li>
                  );
                })}
              </ul>
            ) : null}
            {pages > 1 ? (
              <div className={reprise.rangee}>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((courante) => courante - 1)}
                >
                  {t('reprise.depots.manifeste.precedente')}
                </Button>
                <span className={reprise.discret}>
                  {t('reprise.depots.manifeste.page', { page, pages })}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={page >= pages}
                  onClick={() => setPage((courante) => courante + 1)}
                >
                  {t('reprise.depots.manifeste.suivante')}
                </Button>
              </div>
            ) : null}
            <p className={reprise.discret} aria-live="polite">
              {t('correspondance.nouveau.choisis', { count: choisis.size, max: FICHIERS_MAX })}
            </p>
            <div className={formStyles.champ}>
              <label htmlFor="lot-depot-nature">{t('correspondance.nouveau.nature')}</label>
              <select
                id="lot-depot-nature"
                className={styles.selection}
                value={nature}
                disabled={creer.isPending || cree !== null}
                onChange={(evenement) => setNature(evenement.target.value as NaturePivot)}
              >
                {NATURES_PIVOT.map((valeur) => (
                  <option key={valeur} value={valeur}>
                    {t(`reprise.natures.${valeur}`)}
                  </option>
                ))}
              </select>
              <span className={formStyles.aide}>{t('correspondance.nouveau.natureAide')}</span>
            </div>
          </section>
        ) : null}

        {choisis.size > 0 ? (
          <section className={styles.temps} aria-labelledby="lot-depot-3">
            <h3 id="lot-depot-3" className={styles.tempsTitre}>
              <span className={styles.tempsNumero} aria-hidden="true">
                3
              </span>
              {t('correspondance.nouveau.profilage')}
            </h3>
            <p className={reprise.texte}>{t('correspondance.nouveau.profilageTexte')}</p>
          </section>
        ) : null}

        {erreur ? (
          <p className={reprise.erreur} role="alert">
            {cree ? t('correspondance.nouveau.profilageRefuse', { message: erreur }) : erreur}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
